/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
}                             from '@angular/core';

import {
  ActivatedRoute,
  NavigationEnd,
  Router
}                             from "@angular/router";

import {first}                from "rxjs/operators";
import {Subscription}         from "rxjs";

import {
  Feature,
  FeatureCollection,
  Geometry
}                             from "geojson";

import {ContextMenu}          from 'primeng/contextmenu';
import {MenuItem}             from 'primeng/api';

import {PanoramaService}      from "@/services/panorama.service";
import {ActionService}        from "@/services/action.service";

import {ResetLeaflet}         from "@/components/leaflet/actions/reset.leaflet";
import {ZoomInLeaflet}        from "@/components/leaflet/actions/zoom.in.leaflet";
import {ZoomOutLeaflet}       from "@/components/leaflet/actions/zoom.out.leaflet";
import {ViewingCone}          from "@/components/leaflet/viewing.cone";
import {OverviewNavigateById} from "@/components/leaflet/actions/overview.navigate.by.id";
import {LeafletLoadJob}       from "@/components/leaflet/leaflet.load.job";

import {ActionType}           from "@/models/action.type";
import {Panorama}             from "@/models/panorama";
import {Subdivision}          from "@/models/subdivision";
import {ViewOrientation}      from "@/models/view.orientation";
import {PanoramaSelection}    from "@/models/panorama.selection";

import {DistinctColorPalette} from "@/helpers/distinct.color.palette";
import {errorAsText}          from "@/util/util";
import {Logger}               from '@/util/logger';

import * as $                 from 'jquery';

import * as L                 from 'leaflet';
import {
  LatLngBounds,
  Layer,
  LeafletEvent,
  LeafletKeyboardEvent,
  LeafletMouseEvent,
  Marker,
  ResizeEvent
}                             from 'leaflet';
import 'leaflet.markercluster';

@Component({
  standalone: false,
  selector: 'leaflet-viewer-wrapper',
  template: `
    <div class="leaflet-viewer-component-bg-wrapper">

      <div id="load-progress-thin-bar" class="load-progress-thin-bar"></div>

      <!-- the leaflet inset controls zoom/reset -->
      <leaflet-controls *ngIf="isMiniMap()" mode="minimap"></leaflet-controls>

      <div id="leaflet-viewer-component-{{uuid}}" class="leaflet-viewer-component">

        <div id="leaflet-prefill" class="leaflet-prefill">
          <h3>Network Overview</h3>

          <p style="width: 450px;">
            Please wait while network data loads (should be less than a few seconds).  You will only be able to see network
            data that you have been assigned permission to view.  If no network data is loaded or an error is shown,
            please contact your project administrator or Iron Road for assistance.
          </p>

        </div>


        <p-contextMenu baseZIndex="500000" id="contextMenu" [model]="markerMenu" styleClass="markermenu"></p-contextMenu>

      </div>


    </div>
  `
})


/**
 *
 * @class LeafletViewer
 *
 * @brief a map window, we use Leaflet as our map viewer
 *
 *   documentation: https://leafletjs.com/reference.html
 *
 *        tutorial: https://leafletjs.com/examples/quick-start/
 *
 *         geojson: https://geojson.org/
 *                  https://leafletjs.com/examples/geojson/
 *
 *       clustered: https://github.com/Leaflet/Leaflet.markercluster
 *                  https://stackblitz.com/edit/ts-leaflet-markercluster-d6m5wv?file=index.ts
 *
 * @note we use this same component for both a full page map viewer (the home/landing page or
 * "overwatch" window), and we also use it for the "minimap" view, as a zoomed out selector
 * panel in the lower left corner we can use to navigate from panorama to panorama when we
 * are "walking" the rail tracks.
 *
 * So there are two modes of operation here; either minimap mode or overwatch mode.  But both
 * use all the same data and initialization code etc.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2022
 *
 */
export class LeafletViewer implements OnInit, AfterViewInit, OnDestroy {

  private static instance: number                 = 0;

  public uuid: number                             = 0;

  /** leaflet map API */
  private _map: any                               = null;

  /** the corridor routes for overview */
  private _routes: any                            = null;

  /** the panoramic point data */
  private _panoData: any                          = null;

  /** make true to log all events (verbose mode) */
  private _loggingEvents: boolean                 = false;

  /** the corridors - "routes" layer */
  private _routesLayer: L.GeoJSON                 = null;

  /** the layer for drawing our viewing cone */
  private _viewConeLayer: ViewingCone             = null;

  /** the layer for markers on the minimap */
  private _miniMarkers: L.LayerGroup              = null;

  /** the clustered markers layer */
  private _clusteredMarkers: L.MarkerClusterGroup = null;

  @Input() mode                                   = 'overwatch';

  /** events spawned from Leaflet */
  @Output() leaflet_event                         = new EventEmitter<LeafletEvent>();

  /** we sent out a Panorama event when we want to viewer to jump in sync with us */
  @Output() pano_jump                             = new EventEmitter<Panorama>();

  /** the loading progress */
  private _loadProgress: number                   = 0.0;

  /** the loading progress message */
  private _loadStatus: string                     = '';

  /** the current panorama we are focused on */
  private _panorama: Panorama                     = null;

  /** our subscription to routing events */
  private _routingSubscription: Subscription      = null;

  /** our subscription to panorama changes */
  private _panoSubscription: Subscription         = null;

  /** have tracked any pan/zoom...beyond the very first one? */
  private _hasMoved: boolean                      = false;

  /**
   * we subscribe to 'moveend' so we can update the map when some kind of
   * pan/zoom happens.  But, that event is also generated by just setting
   * the focus to a new panorama (i.e. implicit call to setView() on the
   * map).  So we have to be able to get the first 'moveend' event, so
   * we don't refresh twice just because of setting a new panorama.
   *
   */
  private _ignoreOneMove: boolean                 = false;

  /** the raw geometry for the corridor lines */
  private _collection: FeatureCollection          = null;

  /** the GeoJSON features that are the corridor lines */
  private _features: Feature[]                    = [];

  /** the actual context menu */
  @ViewChild(ContextMenu)
  private contextMenu: ContextMenu;

  /** the context menu for markers */
  public markerMenu: MenuItem[]                  = [];

  /** standard constructor */
  constructor(
    private panos: PanoramaService,
    private actions: ActionService,
    private route:  ActivatedRoute,
    private router: Router
  ) {

    let oc: LeafletViewer = this;

    LeafletViewer.instance++;

    oc.uuid = LeafletViewer.instance;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} ctor...`);

    /*
     * listen for router events; when we get re-loaded because of route reuse,
     * angular doesn't call things like AfterViewInit...it just directly hot
     * swaps the component for the route back in.  But we may still need to
     * do things anytime the path is loaded; regardless of first load or
     * cached re-load.
     *
     */

    oc._routingSubscription = oc.router.events.subscribe(e => {

      if (e instanceof NavigationEnd) {

        Logger.debug(`[LeafletViewer] {${oc.uuid}} routing ended`, e);

        if (!oc.isMiniMap()) {

          if (oc._hasMoved) {

            /* they are just panning/zooming the map */

            Logger.debug(`[LeafletViewer] {${oc.uuid}} OVERWATCH URL updated from pan/zoom...`);

          } else {

            /*
             * the subscription may be firing, even though component itself has been detached;
             * so we check if it's actually to overwatch URL or not, if it's not...don't do anything.
             *
             */

            if (! /\/^overview/.test(e.url)) {

              return ;
            }

            /* reset the actions; the actions in the panorama service may be for the minimap */

            Logger.debug(`[LeafletViewer] {${oc.uuid}} OVERWATCH, (re)load - setting actions...`);

            oc.setupActions();

            /* we're in overwatch mode, make sure there is no subdivision/mile selected and redraw if needed */

            setTimeout(() => {

              Logger.debug(`[LeafletViewer] {${oc.uuid}} (end of routing) clearing panorama selection...`);

              oc.panos.select();

              oc.panos.refresh(true);

            }, 50);
          }
        }
      }

    });


  }

  /** check if we are in minimap mode */
  isMiniMap(): boolean {

    let lv: LeafletViewer = this;

    return lv.mode != "overwatch";
  }

  /** fetch the current loading progress */
  progress(): string {

    return this._loadProgress.toFixed(2);
  }

  /** force the progress value */
  setProgress(progress: number) {

    let lv: LeafletViewer = this;
    let el: JQuery<HTMLElement> = $(`.load-progress-thin-bar`);

    lv._loadProgress = progress;

    $(el).css('width', `${lv.progress()}%`);

  }

  /** force the current status message */
  setStatus(msg: string) {

    this._loadStatus = msg;
  }

  routes(): any {

    return this._routes;
  }

  /** force the routes layer */
  setRouteLayer(l: L.GeoJSON) {

    this._routesLayer = l;
  }

  /** fetch the routes layer */
  routeLayer(): L.GeoJSON {

    return this._routesLayer;
  }

  /** fetch the current map */
  map(): L.Map {

    return this._map;
  }

  /** fetch the raw panorama data */
  points(): any {

    return this._panoData;
  }

  /** fetch the current URL */
  activeRoute(): ActivatedRoute {

    return this.route;
  }

  /** fetch the current loading status message */
  progressMessage(): string {

    return this._loadStatus;
  }

  current(): Panorama {

    return this._panorama;
  }

  /** fetch the panoramas service */
  panoramas(): PanoramaService {

    return this.panos;
  }

  /** force the clustered makers layer */
  setClusters(c: L.MarkerClusterGroup) {

    this._clusteredMarkers = c;
  }

  /** fetch the clusters layer */
  clusters(): L.MarkerClusterGroup {

    return this._clusteredMarkers;
  }

  /** on page setup */
  ngOnInit() {

    let oc: LeafletViewer = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} init...`);

    /* set up the context menu for markers */

    oc.markerMenu = [{
      label: 'Action 1',
      icon: '',
      command: () => {
        Logger.debug(`[LeafletViewer] bogus action 1, replace me!`)
      }
    }, {
      label: 'Action 2',
      icon: '',
      command: () => {
        Logger.debug(`[LeafletViewer] bogus action 2, replace me!`)
      }
    }];

  }

  ngAfterViewInit() {

    let oc: LeafletViewer = this;

    if (oc.isMiniMap()) {

      Logger.debug(`[LeafletViewer] {${oc.uuid}} MINIMAP after view init...`);

      /* what panorama are we focused on? */

      /*
       * center on the panorama and zoom in to a level where we know we'll see markers
       * and not clusters.  In minimap mode we are "at the bottom" and working with
       * the markers that are visible on screen.  We don't allow them to zoom out...
       * that's what the overwatch page is for.
       *
       */

      oc.miniMapInit();

      return ;
    }

    /* not in minimap mode...do the landing page setup */

    Logger.debug(`[LeafletViewer] {${oc.uuid}} OVERWATCH after view init...`);

    if (oc.panos.data()) {

      /* we already have the global data cached */

      Logger.debug(`[LeafletViewer] {${oc.uuid}} reusing (cached) network data...`);

      oc._routes   = oc.panos.routes();
      oc._panoData = oc.panos.data();

      oc.init();

      /*
       * tell the panorama service to bump any listeners of its cached data,
       * we have to do this to ensure that the search filter comes up,
       * because it pops up when the subdivisions refresh.  We aren't actually
       * refreshing, so we need to bump it.
       *
       */

      setTimeout( () => {

        oc.panos.refresh(false)

      }, 50);


      /* good to go! */

      return ;
    }

    /* the component is up...try to get the data for building an overview map ... */

    Logger.debug(`[LeafletViewer] {${oc.uuid}} first time caching of network data...`);

    oc.panos.overwatch(false)
      .pipe(first())
      .subscribe({
        next: (data: any) => {

          oc._routes   = data.result.routes;
          oc._panoData = data.result.panoramas;

          oc.panos.cacheRoutes(oc._routes);
          oc.panos.cachePanoramaPoints(oc._panoData);

          let subs: Subdivision[] = [];

          for (const [cName, sObj] of Object.entries(data.result.tree)) {

            for (const [sName, tList] of Object.entries(sObj)) {

              let idx: number = subs.findIndex(x => x.name == sName);

              /* if we haven't seen it yet, add it */

              if (idx < 0) {

                subs.push(new Subdivision({name: sName, timelines: tList}));

                continue;
              }

              let sub: Subdivision = subs[idx];

              /* if we already have it, merge the timelines */

              for (let i=0; i<tList.length; i++) {

                sub.addYearMonth(tList[i]);
              }

            }
          }

          Logger.debug(`[LeafletViewer]{${oc.uuid}}   new subdivsion set: `, subs);

          oc.panos.cacheSubdivisions(subs);
          oc.panos.refresh(false);

          oc.init();
        },
        error: (error) => {

          /* something went horribly, horribly wrong! */

          Logger.error(`[LeafletViewer] {${oc.uuid}} can't load: `, errorAsText(error));

          $('#leaflet-prefill').append(`<p style="font-weight: bold; color: red;">${errorAsText(error)}</p>`);

        }
      });

  }

  miniMapInit() {

    let oc: LeafletViewer = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} MINIMAP init...`);

    /*
     * set up the minimap, we can't render the current position and nearby
     * panoramas yet, we have to wait until the KR Panorama viewer sends out
     * panorama change event. Once we get that event we call focusOn() to
     * setup the display for minimap mode, centered on that panorama.
     *
     */

    oc._panoSubscription = oc.panos.onPanorama().subscribe(
      (meta: Panorama) => {

        if (oc._panorama && (oc._panorama.id == meta.id)) {

          Logger.debug(`[LeafletViewer] {${oc.uuid}} panorama change, but already set, ignoring.`);

          return ;
        }

        Logger.debug(`[LeafletViewer] {${oc.uuid}} panorama change...`);

        oc._panorama = meta;

        oc.createMiniView();
      });

  }

  clearMiniView() {

    let oc: LeafletViewer  = this;

    if (oc._miniMarkers) {

      Logger.debug(`[LeafletViewer] {${oc.uuid}} deleting marker layer: `, oc._miniMarkers);

      oc._map.removeLayer(oc._miniMarkers);

      oc._miniMarkers = null;
    }


  }

  refreshMarkers() {

    let oc: LeafletViewer  = this;

    /* get rid of the old markers */

    let zoomLevel: number = oc._map.getZoom();

    Logger.debug(`[LeafletViewer] {${oc.uuid}} refreshing markers z=${zoomLevel}...`);

    oc.clearMiniView();

    if (zoomLevel > 17) {

      /* add a fresh set... */

      oc.createMiniMarkers();

      return ;
    }

    /* zoomed out 1-16, is too dense to show dots, they can just click the corridor line */

    /* also, we still have to update the view cone! */

    oc.setViewCone();

    /* unlock application, we are effectively done rendering */

    oc.actions.action(ActionType.PANO_UNLOCK_APPLICATION).execute();
  }

  /** do whatever we do at the very end of setting up the map widget */
  finalizeRender() {

    let oc: LeafletViewer  = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} FINALIZING...`);

    /* on the mini-map we show a little view cone, so they can see orientation of the panorama */

    if (oc.isMiniMap()) {

      /* lock the viewing cone to the current panorama */

      oc.setViewCone();
    }

    /* replace the background behind the map */

    $(`#leaflet-prefill`).html(`<img style="margin-left: 118px; height: 100%;" src="/assets/images/apple-touch-icon-180x180-precomposed.png">`);

  }

  /** show the popup bubble help with the given content and popup position */
  showBubbleHelp(content: string, x: number, y: number) {

    let pop: HTMLElement = $('#leaflet-popup-panel').get(0);

    $(pop).html(content);

    $(pop).css('left', `${x}px`);
    $(pop).css('top', `${y}px`);

    $(pop).css('display', 'block');
  }

  /** hide the popup help we show when hovering over routes or markers */
  hideBubbleHelp() {

    /* we're done hovering over the route, get rid of the popup */

    let pop: HTMLElement = $('#leaflet-popup-panel').get(0);

    $(pop).css('display', 'none');
  }

  createMiniMarkers() {

    let oc: LeafletViewer  = this;
    let zoomLevel: number  = oc._map.getZoom();

    if (zoomLevel <= 17) {

      /* zoomed out too far, no point drawing markers, it will be an overlapping mess */

      /* unlock application, we are effectively done rendering */

      oc.actions.action(ActionType.PANO_UNLOCK_APPLICATION).execute();

      return ;
    }

    let bbox: LatLngBounds = oc.map().getBounds();
    let t: number          = bbox.getNorth();
    let r: number          = bbox.getEast();
    let b: number          = bbox.getSouth();
    let l: number          = bbox.getWest();

    Logger.debug(`[LeafletViewer] {${oc.uuid}} creating mini markers...`);

    /* get the current sub/timeline selection...if there is one. */

    let sub: string               = '';
    let timeline: string          = '';
    let select: PanoramaSelection = oc.panos.selection();

    if (select) {

      if (select.sub) {

        sub = select.sub;

        if (select.timeline) {

          /* we know the sub/timeline from the selection drop down */

          timeline = select.timeline;

          Logger.debug(`[LeafletViewer] {${oc.uuid}} selected sub/timeline: sub=${sub}, timeline=${timeline}`);
        }
      }
    }

    if (!timeline) {

      /* sub and timeline must be implied from the current panorama */

      if (this.current()) {

        sub      = this.current().sub;
        timeline = this.current().timeline;

        Logger.debug(`[LeafletViewer] {${oc.uuid}} implied sub/timeline: sub=${sub}, timeline=${timeline}`);

      }

    }

    Logger.debug(`[LeafletViewer] {${oc.uuid}} looking for points bounded: sub=${sub}, timeline=${timeline}, t=${t}, r=${r}, b=${b}, l=${l}...`);

    oc.panos.bounded(t, r, b, l, sub, timeline)
      .pipe(first())
      .subscribe(
        (data: any) => {

          /* convert to panorama meta data objects */

          let panos: Panorama[] = [];

          data.result.forEach((item) => {

            panos.push(new Panorama(item));
          });

          /* now we can create markers from the panorama ... */

          Logger.debug(`[LeafletViewer] {${oc.uuid}} received panoramas to make markers on:`, panos);

          /* separate the icon for THIS panorama from the others */

          let currentIcon: L.Icon =
            L.icon({
              iconSize: [12, 12],
              iconAnchor: [6, 6],
              popupAnchor: [2, -16],
              iconUrl: "/assets/images/dot-12-active.png"
            });

          let otherIcon: L.Icon =
            L.icon({
              iconSize: [12, 12],
              iconAnchor: [6, 6],
              popupAnchor: [2, -16],
              iconUrl: "/assets/images/dot-12-inactive.png",
            });

          let disabledIcon: L.Icon =
            L.icon({
              iconSize: [12, 12],
              iconAnchor: [6, 6],
              popupAnchor: [2, -16],
              iconUrl: "/assets/images/dot-12-disabled.png",
            });

          let hoverIcon: L.Icon =
            L.icon({
              iconSize: [12, 12],
              iconAnchor: [6, 6],
              popupAnchor: [2, -16],
              iconUrl: "/assets/images/dot-12-hover.png",
            });

          let markers: L.Marker[] = [];

          panos.forEach((p: Panorama) => {

            /* choose the right icon for the marker */

            let icon: L.Icon = otherIcon;

            if (p.id == oc._panorama.id) {

              /* this is exactly where we are now */

              icon = currentIcon;
            }

            if (!p.enabled) {

              icon = disabledIcon;
            }

            /* create the basic marker */

            let marker: L.Marker = L.marker(
              new L.LatLng(p.y, p.x), {
                icon,
              });

            /* remember the metadata */

            marker['data'] = p;

            /* add hover behavior */

            marker.on('mouseover', (e: LeafletMouseEvent) => {

              e.originalEvent.preventDefault();

              let m: L.Marker = e.target;

              m.setIcon(hoverIcon);

              oc.popMarkerHelp(m, e.originalEvent);

            });

            marker.on('mouseout', (e: LeafletMouseEvent) => {

              e.originalEvent.preventDefault();

              let icon: L.Icon = otherIcon;
              let m: L.Marker  = e.sourceTarget;

              if (m['data'].id == oc._panorama.id) {

                /* this is exactly where we are now */

                icon = currentIcon;
              }

              m.setIcon(icon);

              /* can hide any marker bubble help we're showing */

              oc.hideBubbleHelp();

            })

            marker.on('contextmenu', (evt: LeafletMouseEvent) => {

              let m: Marker     = evt.target;
              let e: MouseEvent = evt.originalEvent;

              oc.showMarkerContextMenu(m, e);

            });


            /* add the click behavior (i.e. jump to the other panorama) */

            marker.on('click', (e:LeafletMouseEvent) => {

              e.originalEvent.preventDefault();

              let m: L.Marker = e.target;
              let p: Panorama = m['data'];

              if (p.id == oc._panorama.id) {

                /* wherever you go, there you are. */

                Logger.debug(`[LeafletLoadJob] null jump, already here.`);

                return ;
              }

              oc.miniJump(p);

            });

            /* save the marker */

            markers.push(marker);

          });

          /* add the markers as a layer ... */

          oc._miniMarkers = L.layerGroup(markers);

          oc._miniMarkers.setZIndex(50);
          oc._miniMarkers.addTo(oc._map);

          oc.finalizeRender();

          /* unlock application, we are effectively done rendering */

          oc.actions.action(ActionType.PANO_UNLOCK_APPLICATION).execute();
        },
        (error) => {

          /* unlock application, we are effectively done rendering */

          oc.actions.action(ActionType.PANO_UNLOCK_APPLICATION).execute();

          /* something went horribly, horribly wrong! */

          Logger.error(`[LeafletViewer] {${oc.uuid}} can't fetch bounded markers in mini-view: `, errorAsText(error));

          $('#leaflet-prefill').append(`<p style="font-weight: bold; color: red;">${errorAsText(error)}</p>`);

        });

  }

  setupContextMenu(marker: Marker) {

    let oc: LeafletViewer  = this;

    Logger.debug(`[LeafletViewer] building right click menu: `, marker);


  }

  setupRouteGeometry() {

    let oc: LeafletViewer  = this;

    /* build up the GeoJSON feature collection that we use for the route "line strings" */

    oc._features = [];

    DistinctColorPalette.reset();

    oc.routes().forEach((r: any) => {

      let geometry: Geometry = JSON.parse(r.corridor)

      let color: string = DistinctColorPalette.next();

      oc._features.push({
        type: "Feature",
        geometry,
        properties: {
          id:    r.id,
          name:  r.name,
          color
        }
      });

    });

    /* convert our data to GeoJson so we can just create a layer directly from that */

    oc._collection = null;

    oc._collection = {
      type:     "FeatureCollection",
      features: oc._features
    };
  }

  /**
   * Add a layer to the map for the routes. When we are in minimap mode, its simplified;
   * we are just drawing the lines of the major corridors...a summary view.  When we are
   * in fullscreen or overwatch mode though, this method gets called from LeafletLoadJob
   * as part of the background job to load all the details.
   *
   * @note thta setupRouteGeometry() must have already completed regardless of which
   * mode we are in, becasue we need corridor geometries to draw the routes!
   *
   */
  setupRouteLayer(): void {

    let oc: LeafletViewer  = this;

    /*
     * we assume the raw geometry / features for the corridor routes is already created.
     * now we want to actually render the corridor line strings into a layer in the Leaflet
     * map.
     *
     */

    oc._routesLayer = L.geoJSON(oc._collection, {

      onEachFeature: (feature: Feature, layer: Layer) => {

        layer.on('mouseover', (e: LeafletMouseEvent) => {

          e.originalEvent.preventDefault();

          let l = e.target;

          l.setStyle({
            color: 'yellow',
            opacity: .44,
            weight: 10
          });

          let x: number       = e.originalEvent.clientX + window.scrollX;
          let y: number       = e.originalEvent.clientY + window.scrollY - 32;
          let content: string = `
<span class="leaflet-corridor-label">Corridor:</span>
<span class="leaflet-corridor-value">${feature.properties.name}</span>
          `;

          /* show the popup help for routes */

          oc.showBubbleHelp(content, x, y);

        });

        layer.on('mouseout', (e: LeafletMouseEvent) => {

          let l = e.sourceTarget;

          l.setStyle({
            color: feature.properties.color,
            opacity: .44,
            weight: 10
          });

          /* hide the popup help for routes */

          oc.hideBubbleHelp();
        });

        layer.on('click', (e: LeafletMouseEvent) => {

          e.originalEvent.preventDefault();

          Logger.debug(`[LeafletViewer] route click: `, e);

          let sub: string               = '';
          let timeline: string          = '';
          let select: PanoramaSelection = oc.panos.selection();
          let pano: Panorama            = oc.panos.current();

          if (select) {

            if (select.sub) {

              sub = select.sub;

              if (select.timeline) {

                /* we know the sub/timeline from the selection drop down */

                timeline = select.timeline;

                Logger.debug(`[LeafletViewer] {${oc.uuid}} selected (route click) implied: sub=${sub}, timeline=${timeline}`);
              }
            }
          }

          if (!timeline) {

            /* sub and timeline must be implied from the current panorama */

            if (pano) {

              sub      = pano.sub;
              timeline = pano.timeline;

              Logger.debug(`[LeafletViewer] {${oc.uuid}} implied (route click): sub=${sub}, timeline=${timeline}`);

            }

          }

          Logger.debug(`[LeafletViewer] route is looking for nearest: lat=${e.latlng.lat}, lng=${e.latlng.lng}, sub=${sub}, timeline=${timeline}`);

          oc.panos.nearest(e.latlng.lng, e.latlng.lat, sub, timeline)
            .pipe(first())
            .subscribe({
              next: (data: any) => {

                let meta: Panorama = Panorama.from(data.result);

                if (oc.isMiniMap()) {

                  if (meta.id == oc._panorama.id) {

                    /* wherever you go, there you are. */

                    Logger.debug(`[LeafletLoadJob] null jump, already here.`);

                    return;
                  }

                  Logger.debug(`[LeafletLoadJob] MINIMAP line string jump to: `, meta);

                  oc.miniJump(meta);

                } else {

                  Logger.debug(`[LeafletLoadJob] OVERWATCH line string jump to: `, meta);

                  oc.navigateTo(meta.id, meta.y, meta.x);
                }
              },
              error: (error) => {

                /* something went horribly, horribly wrong! */

                Logger.error(`[LeafletViewer] can't find nearest panorama: `, errorAsText(error));

                $('#leaflet-prefill').append(`<p style="font-weight: bold; color: red;">${errorAsText(error)}</p>`);

              }
            });

        });

      },

      style: (feature) => {
        return {
          color: feature.properties.color,
          opacity: .44,
          weight: 10

        };
      }

    });

    oc._routesLayer.addTo(oc._map);

  }

  createMiniRoutes() {

    let oc: LeafletViewer  = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} creating mini routes...`);

    oc.panos.corridors()
      .pipe(first())
      .subscribe({
        next: (data: any) => {

          oc._routes   = data.result.routes;

          oc.panos.cacheRoutes(oc.routes());

          oc.setupRouteGeometry();

          oc.setupRouteLayer();

          /*
           * we don't have to (and should not) draw the mini-markers; we always refresh
           * the mini-marker layer after the end of any pan/zoom (i.e. when you set
           * a location in leaflet).  The reason for this is that leaflet might be
           * animating the pan/zoom...so it might not actually be at the location that
           * was just set...yet.
           *
           * So if you try to draw the markers now, before the animation finishes,
           * it would create timing issues, maybe the results come back before or
           * after the animation finishes and maybe the bounding box you ask for
           * is somewhere between the original position and the position at which
           * you want to draw the mini markers.
           *
           * It matters a lot because if you ask for mini markers that fall in side
           * the old bounding box...when the dust settles, you will only be showing
           * some markers, i.e. which ever ones happen to be in both the old
           * AND new bounding box.
           *
           * To avoid any such timing issues, we just delay when we ask for and
           * update the markers, we ALWAYS refresh markers on the 'moveend' event,
           * because leaflet kicks that out after it's done animating any pan/zoom.
           *
           * For the case of first page/component load; we do a setView() on
           * leaflet just to force the pan/zoom animation to kick in. We then
           * handle marker refreshing in 'moveend' as usual.
           *
           */

          const lat              = oc._panorama.y;
          const lng              = oc._panorama.x;

          oc._map.setView([lat, lng], oc._map.getZoom(), {
            "animate": true
          });

        },
        error: (error) => {

          /* something went horribly, horribly wrong! */

          Logger.error(`[LeafletViewer] {${oc.uuid}} can't fetch corridors in mini-view: `, errorAsText(error));

          $('#leaflet-prefill').append(`<p style="font-weight: bold; color: red;">${errorAsText(error)}</p>`);

        }
      });

  }

  createMiniView() {

    let oc: LeafletViewer  = this;
    const lat              = oc._panorama.y;
    const lng              = oc._panorama.x;

    /* tell the 'moveend' listener to ignore the setView() we are about to do...so we don't refresh twice */

    /*
    oc._ignoreOneMove = true;
    */

    /* if we already have the map setup, don't recreate everything...just adjust our position */

    if (oc._map) {

      oc._map.setView([lat, lng], oc._map.getZoom(), {
        "animate": true
      });

      Logger.debug(`[LeafletViewer] {${oc.uuid}} markers will be refreshed on 'moveend': `, oc._panorama);

      /*
      oc.refreshMarkers();
      */

      return;
    }

    /* create the mini-map, focused on this panorama */

    Logger.debug(`[LeafletViewer] {${oc.uuid}} creating mini view...`);

    oc._map  = L.map(`leaflet-viewer-component-${oc.uuid}`, {
      center: [ lat, lng ],
      zoom: 18,
      zoomControl: false
    });

    let bbox: LatLngBounds = oc.map().getBounds();
    let t: number          = bbox.getNorth();
    let r: number          = bbox.getEast();
    let b: number          = bbox.getSouth();
    let l: number          = bbox.getWest();

    Logger.debug(`[LeafletViewer] {${oc.uuid}} MINIMAP, focusing on panorama (${t},${l} - ${b},${r}):`, oc._panorama);

    /*
     * initialization generally follows these steps:
     *
     * setup tiles - This is the actual map background imagery to show, we normally just use OpenStreetMap
     * tiles. In theory, you could use other tile sources for things like satellite view etc.  This has
     * nothing to do with the panoramic views; this is just "how to draw the map".
     *
     * setup events - Register our callback handlers for things like clicking on the map, keyboard hot
     * keys etc.
     *
     * setup actions - Additional actions like re-setting the map view
     *
     * create the routes - This is the heart of our viewer; we have to draw routes onto the map,
     * which are the railway tracks, on which we have positions where panorama views have been
     * taken.  Our viewer basically allows users to click into any of those positions and open
     * a real-life panoramic view of the railway track at that position.  So the routes and the
     * markers on the routes are critical as that is the primary way for users to navigate around
     * the thousands and thousands of panoramic views that have been taken.
     *
     *
     */

    oc.setupTiles();

    oc.setupEvents();

    oc.setupActions();

    /* get rid of any old layers/markers for mini view */

    oc.clearMiniView();

    /*
     * setup for the current panorama in focus and the bounds of the map at
     * the current pan/zoom level, i.e. show whatever markers fall within
     * the bounds of the map.
     *
     */

    /*
     * add a layer for the corridors (it will chain to creating other layers as
     * needed.  in fullscreen or "overwatch" mode, we do more detailed route
     * setup, via the load() method.
     *
     */

    oc.createMiniRoutes();

    /*
     * in the mini-map we have to listen for zoomend and moveend; i.e. when they finish
     * doing any combination of panning and zooming.  At that point, we have to recalculate
     * the points we're showing in the minimap. We only ever fetch and show panorama points
     * that actually fit on the map in view.  Also, if hey zoom out too much...we just don't
     * show any panorama points; it would be a mess of dots, and they can click on the route
     * lines anyway (and it will just jump to the nearest panorama).
     *
     */

    oc._map.on('moveend', () => {

      /*
      if (oc._ignoreOneMove) {

        Logger.debug(`[LeafletViewer] {${oc.uuid}} MINIMAP finished moving, ignoring after creating.`);

        oc._ignoreOneMove = false;

        return ;
      }
      */

      Logger.debug(`[LeafletViewer] {${oc.uuid}} MINIMAP finished moving, refreshing markers...`);

      oc._hasMoved = true;

      /* refresh the minimap markers ... */

      oc.refreshMarkers();
    })

  }

  setupTiles() {

    let oc: LeafletViewer = this;

    /* add tiles of choice */

    Logger.debug(`[LeafletViewer] {${oc.uuid}} adding tiles...`);

    /* ref: https://gis.stackexchange.com/questions/263824/leaflet-basemap-zoom-to-maximum */

    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 30,
      maxNativeZoom: 19,
      minZoom: 3,
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    });

    tiles.addTo(oc._map);
  }

  setupEvents() {

    let oc: LeafletViewer = this;

    /* add event handling */

    oc._map.on('resize',      (e: ResizeEvent)          => {oc.publishEvent(e);});
    oc._map.on('zoomend',     (e: LeafletEvent)         => {oc.publishEvent(e);});
    oc._map.on('moveend',     (e: LeafletEvent)         => {oc.publishEvent(e);});
    oc._map.on('contextmenu', (e: LeafletMouseEvent)    => {oc.publishEvent(e);});
    oc._map.on('click',       (e: LeafletMouseEvent)    => {oc.publishEvent(e);});
    oc._map.on('dblclick',    (e: LeafletMouseEvent)    => {oc.publishEvent(e);});
    oc._map.on('keydown',     (e: LeafletKeyboardEvent) => {oc.publishEvent(e);});
    oc._map.on('keyup',       (e: LeafletKeyboardEvent) => {oc.publishEvent(e);});

  }

  setupActions() {

    let oc: LeafletViewer = this;

    /* register our actions */

    oc.actions.add(new ResetLeaflet(oc));
    oc.actions.add(new ZoomInLeaflet(oc));
    oc.actions.add(new ZoomOutLeaflet(oc));
    oc.actions.add(new OverviewNavigateById(oc));
  }

  /** set up the Leaflet based map window for overwatch mode (user login landing page) */
  init() {

    let oc: LeafletViewer = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} OVERWATCH init...`);

    oc._map  = L.map(`leaflet-viewer-component-${oc.uuid}`, {
      center: [ 39.8282, -98.5795 ],
      zoom: 3,
      zoomControl: false
    });

    /* tell the 'moveend' listener to ignore the setView() we are about to do...so we don't refresh twice */

    oc._ignoreOneMove = true;

    /*
     * if they pan/zoom, we trigger a "self routing" to get the query parameters
     * updated, with the new center of the map and zoom level...so that they can
     * always bookmark where they are, just by copy/pasting the URL that is
     * displayed in the browser address bar.
     *
     */

    oc._map.on('moveend', () => {

      if (oc._ignoreOneMove) {

        Logger.debug(`[LeafletViewer] {${oc.uuid}} OVERWATCH, finished moving bug ignoring initial move (load).`);

        oc._ignoreOneMove = false;

        return ;
      }

      oc._hasMoved = true;

      let rx: number = oc._map.getCenter().lat;
      let ry: number = oc._map.getCenter().lng;
      let rz: number = oc._map.getZoom();

      this.router.navigate([], {
        relativeTo:  this.route,
        queryParams: {
          rx,
          ry,
          rz
        },
        queryParamsHandling: 'merge'
      });

    });

    /* general setup ... */

    setTimeout(() => {

      /* wait a bit for the map to take over the screen and then show the control button bar */

      oc.actions.action(ActionType.LEAFLET_CONTROLS_SHOW).execute();

    }, 500);

    /*
     * initialization generally follows these steps:
     *
     * setup tiles - This is the actual map background imagery to show, we normally just use OpenStreetMap
     * tiles. In theory, you could use other tile sources for things like satellite view etc.  This has
     * nothing to do with the panoramic views; this is just "how to draw the map".
     *
     * setup events - Register our callback handlers for things like clicking on the map, keyboard hot
     * keys etc.
     *
     * setup actions - Additional actions like re-setting the map view
     *
     * create the routes - This is the heart of our viewer; we have to draw routes onto the map,
     * which are the railway tracks, on which we have positions where panorama views have been
     * taken.  Our viewer basically allows users to click into any of those positions and open
     * a real-life panoramic view of the railway track at that position.  So the routes and the
     * markers on the routes are critical as that is the primary way for users to navigate around
     * the thousands and thousands of panoramic views that have been taken.
     *
     *
     */

    oc.setupTiles();

    oc.setupEvents();

    oc.setupActions();

    oc.load();
  }

  /**
   * Create the details routes with markers to use for navigation between panoramas. Note
   * that unlike the minimap view which is based on the high level corridors, here we have
   * to add full details...which is a LOT more processing. So it has to be made an
   * incremental task that we can just chip away at in the background.  Otherwise, we would
   * be blocking the main GUI for 3-5 seconds.
   *
   */
  load() {

    let oc: LeafletViewer = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} loading model...`);

    oc.setProgress(0.0);

    oc._loadStatus   = "Loading model...";

    /* clean any existing layers */

    oc.clearLayers();

    /* create the layers, this will launch the long-running load/setup as background work. */

    oc.createRoutes();

  }

  /* publish the KRPano events to any observers */
  publishEvent(e: LeafletEvent) {

    let oc: LeafletViewer   = this;

    if (oc._loggingEvents) {

      Logger.debug(`[LeafletViewer] {${oc.uuid}} publishEvent - `, e);
    }

    /* emit the event to parent components that are watching */

    oc.leaflet_event.emit(e);
  }

  createRoutes() {

    let oc: LeafletViewer   = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} createRoutes ... `);

    /*
     * we create the routes and clusters as a background job, so we don't block the GUI.
     * it can take 3-5 seconds for clustering 50-60K panorama points.
     *
     */

    let job: LeafletLoadJob = new LeafletLoadJob(oc);

    job.doWork();

  }

  clearLayers() {

    let oc: LeafletViewer = this;

    if (oc._routesLayer) {

      oc._map.removeLayer(oc._routesLayer);

      oc._routesLayer = null;
    }

    if (oc._clusteredMarkers) {

      oc._map.removeLayer(oc._clusteredMarkers);

      oc._clusteredMarkers = null;
    }

    oc.clearViewCone();

  }

  /** for a given marker, show its user help/info popup */
  popMarkerHelp(marker: Marker, event: MouseEvent) {

    let oc: LeafletViewer = this;

    let x: number       = event.clientX + window.scrollX;
    let y: number       = event.clientY + window.scrollY - 32;
    let p: any          = marker['data'];

    let stamp: Date   = null;
    let ts: string    = '';

    if (typeof p.stamp == 'string') {

      stamp = new Date(p.stamp);

    } else {

      stamp = p.stamp as Date;
    }

    /* it's an actual date */

    ts = stamp.toLocaleString(
      'en-us',
      {
          hour12: false
      }).slice(0, 19).replace('T', ' ') + '.' + stamp.getMilliseconds();

    let optHeight = '';
    let optUUID   = '';

    if (typeof p.height != 'undefined') {

      optHeight = `<span class="leaflet-marker-value"><b>Elevation:</b> ${p.height.toFixed(6)}</span>`;
    }

    if (typeof p.uuid != 'undefined') {

      optUUID = `<span class="leaflet-marker-value" style="font-size: 9px;">UUID: ${p.uuid}</span>`;
    }

    let content: string = `
<span class="leaflet-marker-label">[${p.x.toFixed(6)}, ${p.y.toFixed(6)}]:</span>
<span class="leaflet-marker-value"><b>Mile:</b> ${p.mile.toFixed(4)}</span>
<span class="leaflet-marker-value"><b>Heading:</b> ${p.heading.toFixed(6)}</span>
${optHeight}
<span class="leaflet-marker-value"><b>Timestamp:</b> ${ts}</span>
<span class="leaflet-marker-value"><b>Subdivision:</b> ${p.sub}</span>
${optUUID}
          `;

    oc.showBubbleHelp(content, x, y);
  }

  showMarkerContextMenu(marker: Marker, event: MouseEvent) {

    let oc: LeafletViewer = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} marker right click: `, [marker, event]);

    /* hide the bubble help, just in case its being shown already */

    oc.hideBubbleHelp();

    /* build up the context menu for a marker */

    oc.setupContextMenu(marker);

    /* show it! */

    oc.contextMenu.show(event);

  }

  clear() {

    let oc: LeafletViewer = this;

    if (!oc._map) {

      /** nothing to do */

      return ;
    }

    Logger.debug(`[LeafletViewer] {${oc.uuid}} removing map.`);

    oc._map.off();
    oc._map.remove();

    oc._map = null;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} removed.`);

  }

  reset() {

    let oc: LeafletViewer  = this;

    if (oc.isMiniMap()) {

      /* zoom back to the current panorama */

      Logger.debug(`[LeafletViewer] {${oc.uuid}} MINIMAP resetting view...`);

      oc.createMiniView();

      return ;
    }

    /* zoom back to where we can see all the routes */

    Logger.debug(`[LeafletViewer] {${oc.uuid}} OVERWATCH resetting view...`);

    let bbox: LatLngBounds = oc._routesLayer.getBounds();
    bbox.pad(0.02);

    oc._map.fitBounds(bbox);

    Logger.debug(`[LeafletViewer] {${oc.uuid}} reset.`);


  }

  /** set the current viewing cone to the current panorama */
  setViewCone() {

    Logger.debug(`[LeafletViewer] resetting viewing cone...`);

    let oc: LeafletViewer  = this;

    /*
     * when we (re)set the view cone, retain the old viewing angle if possible,
     * so there is less noticeable "twitching" of the view cone as the user moves
     * around.
     *
     */

    let bearing: number    = 0;
    let offset: number     = 0;

    if (oc._viewConeLayer) {

      bearing = oc._viewConeLayer.bearing();
      offset  = oc._viewConeLayer.offset();
    }

    oc.clearViewCone();

    oc._viewConeLayer = new ViewingCone(oc.current(), bearing, offset);
    oc._viewConeLayer.addTo(oc.map());
  }

  /** clear the viewing cone layer */
  clearViewCone() {

    let oc: LeafletViewer  = this;

    if (!oc._viewConeLayer) {

      /* nothing to do */

      return;
    }

    oc._viewConeLayer.clear();

    oc._map.removeLayer(oc._viewConeLayer);

    oc._viewConeLayer = null;
  }

  /** track the viewing cone angle with hte KR Pano viewing direction of the user */
  viewChanged(orientation: ViewOrientation) {

    let oc: LeafletViewer  = this;

    /* we only need the horizontal rotation; the mini-map is 2D! */

    if (oc._viewConeLayer) {

      oc._viewConeLayer.setOffset(orientation.h);
    }
  }

  /**
   * unlike the navigateTo() method, we aren't reloading / loading the page, we just want
   * to adjust to focus on this other panorama.
   *
   */
  miniJump(p: Panorama) {

    let oc: LeafletViewer  = this;

    /*
     * the KR Pano viewer will tell us when it has moved to a new panorama, and we can then
     * do what we usually do in response.  But for that to happen, we have to tell the KR Pano
     * viewer to jump.
     *
     */

    oc.pano_jump.emit(p);
  }

  /** jump to another panorama view */
  navigateTo(id: number, x: number=0.0, y: number=0.0, z: number=0.0) {

    let oc: LeafletViewer  = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} navigating directly to: `, id);

    /*
     * reset our "has moved" logic, so that when we come back later, we have to start in
     * our initial state of having not yet moved.
     *
     */

    oc._hasMoved      = false;

    /* remember where we were on the overwatch screen */

    if (x == 0.0) {

      x = oc._map.getCenter().lat;
    }

    if (y == 0.0) {

      y = oc._map.getCenter().lng;
    }

    if (z == 0.0) {

      z = oc._map.getZoom();
    }

    oc.panos.panoramaNavigate(id, x, y, z);
  }

  zoomIn() {

    let oc: LeafletViewer  = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} resetting zooming in...`);

    oc._map.zoomIn();
  }

  zoomOut() {

    let oc: LeafletViewer  = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} resetting zooming out...`);

    oc._map.zoomOut();
  }

  ngOnDestroy() {

    let oc: LeafletViewer  = this;

    Logger.debug(`[LeafletViewer] {${oc.uuid}} ngOnDestroy ...`);

    oc.clear();

    /* clean up our routing subscription */

    if (oc._routingSubscription) {

      oc._routingSubscription.unsubscribe();
      oc._panoSubscription.unsubscribe();
    }
  }

}
